/**
 * **一条可横滚的工具条**（窗口的基础能力）。
 *
 * ## 为什么放在内核
 *
 * 真机反馈："这个功能应该是窗口的基础功能，不是某个窗口的功能"。
 * 三处都要它、以后还会有更多：
 *   · 窗口头部（`NpArea` 的 `.np-area__header-scroll`）；
 *   · 包自己的**二级行**（打印包编辑器那条工具栏）；
 *   · 以后的面板标题条 / 状态条。
 *
 * ## 两个必须一起给的东西
 *
 * 纯 CSS 的 `overflow-x: auto` **只认触摸横滑**：
 *   · **鼠标滚轮**在横向容器上是"没有可滚的方向"—— 垂直滚轮的 `deltaY`
 *     得借给 `scrollLeft`（有这个函数的原因）；
 *   · 触摸要 `touch-action: pan-x`（否则手指横划会被当成按在按钮上）。
 * CSS 那个类在 `np-strip.css`，行为在这个函数里，两边配套用。
 *
 * ⚠️ 滚到头时**不** `preventDefault`：让事件继续冒泡 ——
 * 否则"在工具条上滚一下，页面/画布纹丝不动"会显得像卡住了。
 */
export interface StripScrollOptions {
  /**
   * 锚点：滚轮事件挂在它上面。
   *
   * 传**函数**取当前元素（工具条常常是 `v-if` 出来的，声明时还为 null）。
   */
  el: () => HTMLElement | null
}

export interface StripScroll {
  onWheel: (ev: WheelEvent) => void
}

export function createStripScroll(options: StripScrollOptions): StripScroll {
  return {
    onWheel(ev: WheelEvent): void {
      const host = options.el()
      if (!host) return
      const max = host.scrollWidth - host.clientWidth
      if (max <= 0) return
      /* 触控板的横向手势优先（`deltaX`），否则把垂直滚动借过来 */
      const delta = Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? ev.deltaX : ev.deltaY
      const next = Math.max(0, Math.min(max, host.scrollLeft + delta))
      if (next === host.scrollLeft) return
      host.scrollLeft = next
      ev.preventDefault()
    },
  }
}
